import { useCallback, useMemo } from 'react';

interface Params {
	totalCurrentPosition: number;
	currentLeverage: number;
	maxQuote: number;
	currentPositionQuote: number;
	maxLeverage: number;
	currentQuote: number;
	sameSide: boolean;
}

/**
 * 4 Cases for Slider logic - scenario => how to calculate leverage pts available
 *
 * Case 1: SameSide && currentLeverage <= maxLeverage
 * 	=> how many pts to get to max leverage
 *
 * Case 2: NOT SameSide && currentLeverage <= maxLeverage
 * 	=> Current position leverage x2 + remaining to get to maxLeverage
 *
 * Case 3: NOT SameSide && currentLeverage > maxLeverage && otherPositions - currentPosition > maxLeverage
 * 	=> strictly reduce current position size
 *
 * Case 4: NOT SameSide && currentLeverage > maxLeverage && otherPositions - currentPosition < maxLeverage
 * 	=> current position + diff between other positions total and maxLeverage
 */
export default function useLeverageSliderEngine({
	totalCurrentPosition,
	currentLeverage,
	maxQuote,
	currentPositionQuote,
	maxLeverage,
	currentQuote,
	sameSide,
}: Params) {
	const { availableLeveragePoints, sliderCase } = useMemo(() => {
		// Calcualte leverage from current position
		const maxPositionAllowed = totalCurrentPosition + maxQuote;

		const currentPositionAbsQuote = Math.abs(currentPositionQuote);
		const maxPositionAbsQuote = Math.abs(maxPositionAllowed);

		const currentPositionPortionOfMaxLeverage = sameSide
			? currentPositionAbsQuote / maxPositionAbsQuote
			: currentPositionAbsQuote /
				(maxPositionAbsQuote - currentPositionAbsQuote);

		const currentPositionLeverageAmount =
			currentPositionPortionOfMaxLeverage * maxLeverage;

		let availableLeveragePoints = Math.abs(
			(maxLeverage - currentLeverage) * 100
		);

		let sliderCase = 1;

		if (sameSide) {
			// case 1
			// do nothing
		} else {
			if (currentLeverage < maxLeverage) {
				// case 2
				availableLeveragePoints =
					(currentPositionLeverageAmount * 2 +
						(maxLeverage - currentLeverage)) *
					100;
				sliderCase = 2;
			} else {
				if (currentLeverage - currentPositionLeverageAmount >= maxLeverage) {
					// case 3
					availableLeveragePoints = currentPositionLeverageAmount * 100;
					sliderCase = 3;
				} else {
					// case 4
					const otherPositionsLeverageAmount =
						currentLeverage - currentPositionLeverageAmount;

					availableLeveragePoints =
						(currentPositionLeverageAmount +
							(maxLeverage - otherPositionsLeverageAmount)) *
						100;
					sliderCase = 4;
				}
			}
		}

		return {
			availableLeveragePoints,
			sliderCase,
		};
	}, [
		currentLeverage,
		currentPositionQuote,
		maxLeverage,
		maxQuote,
		sameSide,
		totalCurrentPosition,
	]);

	const currentLeveragePoint = useMemo(() => {
		const leveragePoint =
			maxQuote === 0 ? 0 : (currentQuote / maxQuote) * availableLeveragePoints;

		// Restrict slider percentage greater than 0, less than max leverage
		return Math.max(Math.min(leveragePoint, availableLeveragePoints), 0);
	}, [availableLeveragePoints, currentQuote, maxQuote]);

	const calcNewQuoteFromLeveragePoint = useCallback(
		({ newPt }: { newPt: number }) => {
			const newQuote = (newPt / availableLeveragePoints) * maxQuote;

			return newQuote;
		},
		[availableLeveragePoints, maxQuote]
	);

	return {
		currentLeveragePoint,
		availableLeveragePoints,
		calcNewQuoteFromLeveragePoint,
		sliderCase,
	};
}
